///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// "Create placemark from this file" offer for dropped point files. When a
/// CSV or GeoJSON drop turns out to be a small, clean set of point
/// locations, a toast offers to save it as one multi-location placemark
/// (the file's rows become member locations) instead of leaving it as an
/// ephemeral dataset browser tab. Accepting creates the placemark, closes
/// the dataset tab, and flies to the framed result.
///

import { ToastAction } from "@/components/ui/toast"
import { toast } from "@/components/ui/use-toast"
import { DataProviderRegistry } from "@/data-providers/DataProviderRegistry"
import { closeDatasetWindow } from "@/redux/dialogSlice"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import {
	cDefaultPlacemarkColor,
	cMaxPlacemarkLocations,
	upsertPlacemark,
	type PlacemarkLocation,
} from "@/redux/placemarksSlice"
import type { AppDispatch } from "@/redux/store"

// Same column heuristics the dataset map layer uses (DatasetActivity)
const cLatKeyPattern = [/lat/i, /^y$/i, /^y[_-]/i]
const cLonKeyPattern = [/lon/i, /^x$/i, /^x[_-]/i]
const cNameKeyPattern = [/^name$/i, /^title$/i, /^label$/i, /^place/i]

// Offer only for clean files: nearly every row must be a valid point
const cMinValidFraction = 0.9

function findKey(keys: string[], patterns: RegExp[]): string | undefined {
	return keys.find(key => patterns.some(pattern => pattern.test(key)))
}

function toNumber(value: unknown): number | undefined {
	const n = typeof value === "number" ? value : parseFloat(String(value))
	return Number.isFinite(n) ? n : undefined
}

function rowName(value: unknown): string | undefined {
	const name = typeof value === "string" ? value.trim() : undefined
	return name && name.length > 0 ? name : undefined
}

/**
 * Extract member locations from parsed file rows if (and only if) the file
 * is a placemark-sized set of points:
 * - GeoJSON: every feature is a Point (the worker flattens a representative
 *   latitude/longitude for *all* geometry types, so the geometry type is
 *   checked explicitly)
 * - CSV: has recognizable lat/lon columns and the values are in range
 * Returns null when the file should stay a plain dataset import.
 */
export function pointLocationsFromRows(
	rows: Record<string, unknown>[],
	kind: "csv" | "geojson"
): PlacemarkLocation[] | null {
	if (rows.length === 0 || rows.length > cMaxPlacemarkLocations) return null

	const keys = Object.keys(rows[0])
	const latKey = kind === "geojson" ? "latitude" : findKey(keys, cLatKeyPattern)
	const lonKey = kind === "geojson" ? "longitude" : findKey(keys, cLonKeyPattern)
	const nameKey = findKey(keys, cNameKeyPattern)
	if (!latKey || !lonKey) return null

	const locations: PlacemarkLocation[] = []
	for (const row of rows) {
		if (kind === "geojson" && row._geometry_type !== "Point") return null

		const lat = toNumber(row[latKey])
		const lon = toNumber(row[lonKey])
		if (lat === undefined || lon === undefined) continue
		if (lat < -90 || lat > 90 || lon < -180 || lon > 180) continue

		locations.push({
			id: crypto.randomUUID(),
			lat,
			lon,
			name: nameKey ? rowName(row[nameKey]) : undefined,
		})
	}

	if (locations.length === 0) return null
	if (locations.length < rows.length * cMinValidFraction) return null
	return locations
}

/**
 * After a CSV/GeoJSON drop finishes importing, offer to convert it into a
 * placemark when the contents qualify. Non-blocking: the normal dataset
 * import has already completed; accepting replaces it.
 */
export function offerPlacemarkFromPoints(
	dispatch: AppDispatch,
	windowId: string,
	filename: string,
	rows: Record<string, unknown>[],
	kind: "csv" | "geojson"
): void {
	const locations = pointLocationsFromRows(rows, kind)
	if (!locations) return

	const name = filename.replace(/\.[^.]+$/, "")
	const count = locations.length

	const create = () => {
		const now = new Date().toISOString()
		const id = crypto.randomUUID()
		dispatch(upsertPlacemark({
			id,
			name,
			locations,
			color: cDefaultPlacemarkColor,
			tags: [],
			createdAt: now,
			updatedAt: now,
		}))

		// The placemark replaces the dataset import: close the drawer tab
		dispatch(closeDatasetWindow(windowId))
		DataProviderRegistry.unregister(windowId)

		// Show it: enable the layer and fly to frame all the members
		dispatch(sendDatasetAction({ type: "placemark", action: "enable" }))
		dispatch(sendDatasetAction({ type: "placemark", action: "selectId", id }))
	}

	toast({
		title: `${count === 1 ? "1 location" : `${count} locations`} found`,
		description: `Save "${name}" as a placemark instead of a data table? It will appear as ${count === 1 ? "a pin" : "pins"} on the globe.`,
		duration: 12000,
		action: (
			<ToastAction altText={`Create placemark from ${name}`} onClick={create}>
				Create placemark
			</ToastAction>
		),
	})
}
